import React, {
	createContext,
	type ReactNode,
	Suspense,
	useCallback,
	useContext,
	useEffect,
	useMemo,
} from 'react';

import { GlobalSideNavigation } from '@atlassian/global-side-navigation/GlobalSideNavigation';
import { GlobalSideNavigationAGGRelayConnection } from '@atlassian/global-side-navigation/GlobalSideNavigationAGGRelayConnection';
import type {
	ShortcutsConfig,
	ShortcutsSlotProps,
} from '@atlassian/global-side-navigation/common/types';
import type { GlobalSideNavigationAGGRelayConnectionQuery$data } from '@atlassian/global-side-navigation/GlobalSideNavigationAGGRelayConnectionQuery.graphql';
import { InvitedAppShortcutsContainer } from '@atlassian/cross-join-nudges/invited-app-shortcuts/invited-app-shortcuts-container/main';
import { useInvitedAppProductLinks } from '@atlassian/cross-join-nudges/services/invited-app-shortcuts/invited-app-product-links';
import type { ShortcutItemsConfig } from '@atlassian/cross-join-nudges/types';
import type { GlobalShortcutLinkItem } from '@atlassian/global-shortcuts/types';
import { useJira } from '@atlassian/global-shortcuts/jira';

import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { fg } from '@confluence/feature-gating';
import { confluenceLocalStorageInstance } from '@confluence/storage-manager/entry-points/storageInstance.ts';
import { PERSISTED_KEYS_ON_SERVER } from '@confluence/storage-manager/entry-points/LocalStorageKeys.ts';
import { expVal, UNSAFE_expValEqualsNoExposure } from '@confluence/feature-experiments';
import { TwoStageHydration } from '@confluence/ssr-utilities/entry-points/TwoStageHydration.ts';
import { AccessStatus } from '@confluence/session-data/entry-points/SiteInformationQuery.ts';
import { SPAViewContext } from '@confluence/spa-view-context/entry-points/SPAViewContext.ts';
import { getCompanyHubSpaceKey } from '@confluence/route-manager/entry-points/companyHubUtils.ts';

import { useNetworkStorageParams } from './hooks/useNetworkStorageParams.ts';
import { useGlobalAppShortcuts } from './hooks/useGlobalAppShortcuts.ts';
import { useForYouConfig } from './hooks/useForYouConfig.tsx';
import { useMenuItemsConfig } from './hooks/useMenuItemsConfig.ts';
import { useResolveInvitedAppShortcuts } from './AppShortcuts/hooks/useResolveInvitedAppShortcuts.tsx';
import { SafeInvitedAppShortcutsRecommendations } from './AppShortcuts/InvitedAppShortcutsRecommendations.tsx';
import { CustomHomeItem } from './components/CustomHomeItem.tsx';
import { useShortcutVisibilityOverrides } from './AppShortcuts/hooks/useShortcutVisibilityOverrides.tsx';
import { useDefaultMenuVisibility } from './hooks/useDefaultMenuVisibility.ts';
import type { MenuVisibility } from './hooks/useDefaultMenuVisibility.ts';

// GlobalSideNavigation persists its resized width to network storage only. The Suspense fallback
// skeleton (SideNavigationSkeleton in @confluence/page-layout) sizes itself from the legacy
// localStorage key PERSISTED_LEFT_SIDEBAR_WIDTH, so without this mirror the skeleton renders at the
// stale/default width and visibly snaps to the real width once the nav loads. Mirroring the
// committed width keeps the skeleton and the loaded nav in sync.
const mirrorSideNavWidthToLocalStorage = ({ finalWidth }: { finalWidth: number }) => {
	confluenceLocalStorageInstance.setItem(
		PERSISTED_KEYS_ON_SERVER.PERSISTED_LEFT_SIDEBAR_WIDTH,
		finalWidth,
	);
};

const hasGlobalAppShortcutsAccess = (accessStatus: AccessStatus | null) =>
	![
		AccessStatus.ANONYMOUS_ACCESS,
		AccessStatus.EXTERNAL_SHARE_ACCESS,
		AccessStatus.EXTERNAL_COLLABORATOR_ACCESS,
		AccessStatus.NOT_PERMITTED,
		null,
	].includes(accessStatus);

// Carries the resolved invited-app-shortcuts result to the ShortcutsSlot component (below), which
// GSN renders deep inside its own tree. Context (rather than a closure) lets the slot component
// live at module scope with a stable identity, so GSN never remounts the shortcuts subtree when the
// resolver result changes reference.
type InvitedAppShortcutsSlotContextValue = {
	resolverResult: ReturnType<typeof useResolveInvitedAppShortcuts>;
	// Whether the invited-app treatment should decorate the shortcuts. This is the divergent,
	// cookie-derived decision — it is applied inside the slot's TwoStageHydration boundary.
	isEnabled: boolean;
};

const InvitedAppShortcutsResolverContext =
	createContext<InvitedAppShortcutsSlotContextValue | null>(null);

/**
 * `ShortcutsSlot` implementation (NAVX-5236) for the invited-app-shortcuts experiment cohort.
 *
 * GSN hands us the already-resolved `shortcutItems` (visibility-filtered, ordered, and
 * analytics-wrapped) plus a pre-bound `Component`. We feed those items to
 * `InvitedAppShortcutsContainer` as its `shortcutsConfig` so it can decorate the eligible
 * invited-app entries in place (hover popup ref, activation onClick, invited label), then render the
 * decorated result back through `Component`.
 *
 * Config keys are opaque here — we read the values straight back via `Object.values`, so we don't
 * depend on `menuItemId` being present on every item variant, and insertion order (hence GSN's
 * visible order) is preserved.
 */
export function InvitedAppShortcutsSlot({
	Component,
	shortcutItems,
}: ShortcutsSlotProps): React.JSX.Element {
	const context = useContext(InvitedAppShortcutsResolverContext);

	const shortcutsConfig = useMemo(
		() =>
			shortcutItems.reduce<ShortcutsConfig>((acc, item, index) => {
				acc[String(index)] = item;
				return acc;
			}, {}),
		[shortcutItems],
	);

	// Fire the experiment exposure only when the enabled cohort is genuinely shown the treatment —
	// i.e. the invited (Jira) shortcut is present in the resolved shortcuts. 'jiraShortcut' is the menu
	// key Confluence uses for it. Runs in an effect so it never fires during SSR / the first (replayed)
	// render — it stays outside the TwoStageHydration boundary and can't affect hydration. `expVal`
	// logs the Statsig exposure; `fireFeatureExposed` emits the product-analytics event.
	const hasInvitedShortcut = shortcutItems.some(
		(item) => 'menuItemId' in item && item.menuItemId === 'jiraShortcut',
	);
	const isExperimentEnabled = context?.isEnabled ?? false;
	const fireFeatureExposed = context?.resolverResult.fireFeatureExposed;
	useEffect(() => {
		if (hasInvitedShortcut && isExperimentEnabled && fireFeatureExposed) {
			fireFeatureExposed({
				isEnabled: expVal('twcg-460-personalised-invite-notifications-m2', 'isEnabled', false),
			});
		}
	}, [hasInvitedShortcut, isExperimentEnabled, fireFeatureExposed]);

	// Whether to decorate the shortcuts with the invited-app treatment is a client-only decision:
	// eligibility is resolved synchronously from a browser cookie the SSR server cannot read, so it is
	// `false` during SSR but can be `true` on the client's first render. Rendering that difference
	// straight into the hydrated tree caused React #418/#422 hydration mismatches — the decorated
	// label injects a <div> into the menu item's label <span> (PUMA-2188 / TWCG-460).
	//
	// TwoStageHydration decouples the first client render from this decision: it replays the exact SSR
	// HTML on the first render (matching the server's undecorated shortcuts) and swaps in the live,
	// decorated tree only AFTER hydration. This mirrors the Nav4 (GlobalNavigation) integration. The
	// slot is rendered on both server and client (see ConfluenceGSN), so the SSR output is captured
	// and can be replayed.
	return (
		<TwoStageHydration name="invited-app-shortcuts-container-gsn">
			{
				// Defensive: the provider always supplies context on this path, but if it is ever missing,
				// fail open to the default (undecorated) shortcuts menu rather than crashing the nav.
				context?.isEnabled && context.resolverResult ? (
					<InvitedAppShortcutsContainer
						shortcutsConfig={shortcutsConfig}
						resolverResult={context.resolverResult}
					>
						{({ referrableShortcuts }) => (
							<Component shortcutItems={Object.values(referrableShortcuts)} />
						)}
					</InvitedAppShortcutsContainer>
				) : (
					<Component />
				)
			}
		</TwoStageHydration>
	);
}

// Carries the productionisation cohort inputs (cloudId, userId) to the productionisation ShortcutsSlot
// component below. Same module-level + context rationale as InvitedAppShortcutsResolverContext. The
// GRS recommendations query itself is read INSIDE GSN by `SafeInvitedAppShortcutsRecommendations`
// (via `useLazyLoadQuery` against the ambient `platformEnv`), so the SSR-preloaded record is served
// synchronously — only the cohort ids travel through context.
const InvitedAppShortcutsProductionisationContext = createContext<{
	cloudId: string;
	userId: string;
} | null>(null);

/**
 * `ShortcutsSlot` implementation (NAVX-5236) for the invited-app-shortcuts productionisation cohort
 * (PUMA-2011).
 *
 * Unlike the experiment slot, eligibility comes from the SSR-preloaded GRS recommendations query
 * rather than the cookie cache. That query is read with `useLazyLoadQuery` inside
 * `SafeInvitedAppShortcutsRecommendations`, which scopes Suspense (cold Relay store) and a fail-open
 * ErrorBoundary to the shortcuts subtree — so the rest of the nav is never blocked.
 */
function InvitedAppShortcutsProductionisationSlot({
	Component,
	shortcutItems,
}: ShortcutsSlotProps): React.JSX.Element {
	const productionisationContext = useContext(InvitedAppShortcutsProductionisationContext);

	const shortcutsConfig = useMemo(
		() =>
			shortcutItems.reduce<ShortcutsConfig>((acc, item, index) => {
				acc[String(index)] = item;
				return acc;
			}, {}),
		[shortcutItems],
	);

	// Defensive: the provider always supplies the cohort inputs on this path; if they are ever
	// missing, fail open to the default (undecorated) menu.
	if (!productionisationContext) {
		return <Component />;
	}

	return (
		<SafeInvitedAppShortcutsRecommendations
			Component={Component}
			shortcutsConfig={shortcutsConfig}
			cloudId={productionisationContext.cloudId}
			userId={productionisationContext.userId}
		/>
	);
}

type PlatformNavigationComponentProps = {
	content?: ReactNode; //react compoenent to show for any given view
	hideGSN?: boolean; //boolean to tell us if the current view should hide the global & GAS section
	scrollTreeToCurrent?: () => void; //function that should be fired whenever the navigation is expanded to scroll to the current pageTree page in the tree once implemented remove optional type
	onExpand?: () => void;
	onCollapse?: () => void;
	onPeekStart?: () => void;
	onPeekEnd?: (args: { trigger: 'mouse-leave' | 'side-nav-expand' }) => void;
	sideNavContentRef?: React.Ref<HTMLDivElement | null>; //optional ref passed in to be attached to SideNavigation platform component
	bottomElem?: ReactNode; // optional element to be rendered at the bottom of the side navigation
};

export const PlatformNavigationComponent = ({
	content,
	onExpand,
	onCollapse,
	onPeekStart,
	onPeekEnd,
	sideNavContentRef,
	hideGSN,
	bottomElem,
}: PlatformNavigationComponentProps): React.JSX.Element => {
	const { accessStatus, cloudId, userId } = useSessionData();
	const networkStorageParams = useNetworkStorageParams();
	const { shortcuts: globalAppShortcuts, createTeamModalComponent } = useGlobalAppShortcuts();
	const forYouConfig = useForYouConfig();
	const itemsConfig = useMenuItemsConfig();
	const { homepageUri } = useContext(SPAViewContext);
	const { isCompanyHubVisible } = useShortcutVisibilityOverrides();
	const defaultMenuVisibility = useDefaultMenuVisibility();

	const isCompanyHubSetAsCustomHome =
		isCompanyHubVisible && homepageUri.includes(getCompanyHubSpaceKey());
	const isCustomHomeVisible: boolean =
		!isCompanyHubSetAsCustomHome && !['/spa.action', '', 'homepageUri'].includes(homepageUri);

	const invitedAppShortcutsExperiment = useResolveInvitedAppShortcuts();
	const isEligibleToSeeInvitedAppShortcuts = useMemo(
		() =>
			// we split the kill-switch gate and targeting gate to avoid the overrides precedence issue
			// eslint-disable-next-line confluence-feature-gating/no-preconditioning
			fg('twcg-460-personalised-invite-notifications-m2-gate') &&
			// Confluence GSN-specific rollout gate. The shared m2 gate above is already at 100%, but
			// wiring invited-app shortcuts into GSN caused SSR hydration errors (PUMA-2188), so the GSN
			// integration is gated separately and can be rolled out independently of Nav4 / Jira.
			// eslint-disable-next-line confluence-feature-gating/no-preconditioning
			fg('twcg-460-1p-notifications-cc-gsn-gate') &&
			invitedAppShortcutsExperiment.ineligibilityReason === null &&
			invitedAppShortcutsExperiment.invitedApps?.jira?.eligible === true,
		[invitedAppShortcutsExperiment],
	);

	const isInvitedAppShortcutsEnabled =
		isEligibleToSeeInvitedAppShortcuts &&
		expVal('twcg-460-personalised-invite-notifications-m2', 'isEnabled', false);

	// GSN reports its hydrated shortcuts via `onShortcutsMenuSettled` but does not cache the resolved
	// product links itself. We derive the invited-app product links from those settled shortcuts and
	// persist them to the cookie cache so the next page load can seed the shortcuts menu during SSR.
	const jiraShortcut: GlobalShortcutLinkItem = useJira('');
	const invitedAppShortcutsConfig: ShortcutItemsConfig = useMemo(
		() => ({ jira: jiraShortcut }),
		[jiraShortcut],
	);
	const { handleUpdateProductLinksFromSettledShortcuts } = useInvitedAppProductLinks({
		shortcutItemsConfig: invitedAppShortcutsConfig,
		getCachedProductLinks: invitedAppShortcutsExperiment.getCachedProductLinks,
		updateHydratedProductLinks: invitedAppShortcutsExperiment.updateHydratedProductLinks,
	});

	const onShortcutsMenuSettled = useCallback(
		({
			shortcuts: hydratedShortcuts,
			visibleMenuIds: _visibleMenuIds,
		}: {
			shortcuts: ShortcutsConfig;
			visibleMenuIds: string[];
		}) => {
			if (
				// Only update the cache if the experiment is ineligible, this is to avoid overwriting the cache when the experiment is eligible
				// A caveat is when the users lose access to the product, the cache will not be updated until expires
				invitedAppShortcutsExperiment.ineligibilityReason !== null
			) {
				invitedAppShortcutsExperiment.updateFromHydratedShortcuts({
					shortcuts: hydratedShortcuts,
					limitToOnlyApsKeys: ['JIRA_SOFTWARE'],
					// In Confluence, we don't respect the visibleMenuIds from the shortcuts menu
					visibleMenuIds: ['jiraShortcut'],
				});
				// Persist the resolved product links to the cookie cache. This does not re-render the
				// current page; the shortcuts menu picks them up on the next page load's SSR read.
				handleUpdateProductLinksFromSettledShortcuts(hydratedShortcuts);
			}
		},
		[invitedAppShortcutsExperiment, handleUpdateProductLinksFromSettledShortcuts],
	);

	// only render global app shortcuts if we meet these conditions
	const showGlobalAppShortcuts = hasGlobalAppShortcutsAccess(accessStatus);

	// Productionisation cohort (PUMA-2011): eligibility comes from the SSR-preloaded GRS
	// recommendations query, rendered through the slot so Suspense/ErrorBoundary stay scoped to the
	// shortcuts subtree. Takes precedence over the experiment cohort. Requires cloudId + userId (the
	// preloader only fired when both were present). Split the kill-switch gate from the experiment (as
	// with the experiment cohort above) so the gate — not the experiment — is preconditioned.
	//
	// Evaluate the experiment WITHOUT firing exposure here: exposure is logged in
	// `InvitedAppShortcutsRecommendations` only when there is an actual recommendation to show
	// (recommendedApps non-empty), so the exposed population reflects users genuinely given the
	// treatment rather than everyone routed into the cohort.
	const isProductionisationEligible =
		showGlobalAppShortcuts &&
		!hideGSN &&
		!!cloudId &&
		!!userId &&
		// eslint-disable-next-line confluence-feature-gating/no-preconditioning
		fg('twcg-460-preload-grs-invited-app-shortcuts-gate');
	const isProductionisationCohort =
		isProductionisationEligible &&
		UNSAFE_expValEqualsNoExposure('twcg-460-preload-grs-invited-app-shortcuts', 'isEnabled', true);

	// Stable identity for the productionisation context value (perf-linting/no-inline-context-value).
	// Null unless both ids are present, so consumers never receive empty-string sentinels — the slot
	// fails open when the value is null (which only happens off the productionisation-cohort path).
	const productionisationContextValue = useMemo(
		() => (cloudId && userId ? { cloudId, userId } : null),
		[cloudId, userId],
	);

	// Note: experiment exposure is fired inside `InvitedAppShortcutsSlot`, and productionisation
	// exposure inside `InvitedAppShortcutsRecommendations` — each only when the treatment is actually
	// shown, so exposure isn't double-fired or attributed to the wrong cohort.

	const gsnProps = {
		cloudId,
		forYouConfig,
		isCustomHomeVisible,
		itemsConfig,
		content,
		onExpand,
		onCollapse,
		onPeekStart,
		onPeekEnd,
		sideNavContentRef,
		globalAppShortcuts,
		onShortcutsMenuSettled,
		invitedAppShortcutsExperiment,
		isInvitedAppShortcutsEnabled,
		isProductionisationCohort,
		showGlobalAppShortcuts,
		networkStorageParams,
		defaultMenuVisibility,
		hideGSN,
		bottomElem,
	};

	return (
		<InvitedAppShortcutsProductionisationContext.Provider value={productionisationContextValue}>
			{/*
			 * Suspense catches useLazyLoadQuery suspensions from GlobalSideNavigationAGGRelayConnection
			 * on pages where no SSR AGG cache was injected (e.g. space settings pages, which are proxied
			 * to the monolith and don't go through Bifrost SSR). Without this boundary, the suspension
			 * would propagate upward — SuspenseWithDataFetcher only adds a Suspense wrapper when
			 * window.__SSR_REACT_STREAMING__ is true, so non-SSR pages have no boundary to catch it,
			 * causing React error 426 and a hard crash.
			 *
			 * Using null fallback is intentional: the GSN will load once the network request resolves.
			 * On SSR pages the cache is always populated so suspension never occurs in practice.
			 */}
			<Suspense fallback={null}>
				<GlobalSideNavigationAGGRelayConnection {...networkStorageParams}>
					{({ queryRef }) => <ConfluenceGSN queryRef={queryRef} {...gsnProps} />}
				</GlobalSideNavigationAGGRelayConnection>
			</Suspense>
			{createTeamModalComponent}
		</InvitedAppShortcutsProductionisationContext.Provider>
	);
};

type ConfluenceGSNProps = {
	queryRef: GlobalSideNavigationAGGRelayConnectionQuery$data;
	cloudId: string | undefined;
	forYouConfig: ReturnType<typeof useForYouConfig>;
	isCustomHomeVisible: boolean;
	itemsConfig: ReturnType<typeof useMenuItemsConfig>;
	content?: ReactNode;
	onExpand?: () => void;
	onCollapse?: () => void;
	onPeekStart?: () => void;
	onPeekEnd?: (args: { trigger: 'mouse-leave' | 'side-nav-expand' }) => void;
	hideGSN?: boolean;
	sideNavContentRef?: React.Ref<HTMLDivElement | null>;
	globalAppShortcuts: ReturnType<typeof useGlobalAppShortcuts>['shortcuts'];
	onShortcutsMenuSettled: (args: { shortcuts: ShortcutsConfig; visibleMenuIds: string[] }) => void;
	invitedAppShortcutsExperiment: ReturnType<typeof useResolveInvitedAppShortcuts>;
	isInvitedAppShortcutsEnabled: boolean;
	isProductionisationCohort: boolean;
	showGlobalAppShortcuts: boolean;
	networkStorageParams: ReturnType<typeof useNetworkStorageParams>;
	defaultMenuVisibility: MenuVisibility[];
	bottomElem?: ReactNode;
};

/**
 * Renders the GlobalSideNavigation with the appropriate props.
 * Extracted as a component (rather than a render function) so React can properly
 * manage its lifecycle and avoid unnecessary re-renders.
 */
function ConfluenceGSN({
	queryRef,
	cloudId,
	forYouConfig,
	isCustomHomeVisible,
	itemsConfig,
	content,
	onExpand,
	onCollapse,
	onPeekStart,
	onPeekEnd,
	sideNavContentRef,
	globalAppShortcuts, //todo: when hideGSN is true we dont' need to query for these
	onShortcutsMenuSettled,
	invitedAppShortcutsExperiment,
	isInvitedAppShortcutsEnabled,
	isProductionisationCohort,
	showGlobalAppShortcuts,
	networkStorageParams,
	defaultMenuVisibility,
	hideGSN,
	bottomElem,
}: ConfluenceGSNProps): React.JSX.Element | null {
	// NetworkStorageParams includes queryRef typed as GlobalSideNavigationAGGRelayConnectionQuery$data.
	// The cast is needed because CI typechecks against pre-built declarations which may not have
	// resolved the Relay-generated type. Runtime behaviour is correct.
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const networkStorageParamsWithQueryRef = { queryRef, ...networkStorageParams } as any;
	// GlobalSideNavigation is rendered with the same props whether or not the invited-app-shortcuts
	// slot is wired; the invited-app treatment is applied entirely inside the slot (via
	// InvitedAppShortcutsSlot + TwoStageHydration below), so the top-level nav structure is identical
	// on the server and on the client's first render.
	const sharedGlobalSideNavigationProps = {
		app: 'confluence' as const,
		customizationStore: 'network' as const,
		testId: 'global-side-navigation',
		appLabel: 'Confluence',
		cloudId,
		preMainElem: hideGSN ? null : isCustomHomeVisible && <CustomHomeItem />,
		forYou: hideGSN ? {} : forYouConfig,
		items: hideGSN ? {} : itemsConfig,
		disableMenuCustomization: hideGSN,
		postItemsElem: content,
		networkStorageParams: networkStorageParamsWithQueryRef,
		shortcuts: hideGSN ? undefined : globalAppShortcuts,
		bottomElem,
		onLeftNavExpand: onExpand,
		onLeftNavCollapse: onCollapse,
		onPeekStart,
		onPeekEnd,
		onResizeEnd: mirrorSideNavWidthToLocalStorage,
		sideNavContentRef,
		defaultMenuVisibility,
		// The GSN cookie-cache seeding is part of the Confluence GSN integration, gated on the same
		// dedicated rollout gate as isEligibleToSeeInvitedAppShortcuts above (PUMA-2188).
		// eslint-disable-next-line confluence-feature-gating/no-preconditioning
		...(fg('twcg-460-personalised-invite-notifications-m2-gate') &&
			fg('twcg-460-1p-notifications-cc-gsn-gate') && {
				experimental_onShortcutsMenuSettled: onShortcutsMenuSettled,
			}),
	};

	const invitedAppShortcutsContextValue = useMemo(
		() => ({
			resolverResult: invitedAppShortcutsExperiment,
			isEnabled: isInvitedAppShortcutsEnabled,
		}),
		[invitedAppShortcutsExperiment, isInvitedAppShortcutsEnabled],
	);

	// Productionisation cohort (PUMA-2011) takes precedence over the experiment cohort. Eligibility
	// comes from the SSR-preloaded GRS recommendations query, read INSIDE the slot by
	// `SafeInvitedAppShortcutsRecommendations` (`useLazyLoadQuery` against `platformEnv`); the cohort
	// ids travel through `InvitedAppShortcutsProductionisationContext` (provided by
	// `PlatformNavigationComponent`).
	if (isProductionisationCohort) {
		return (
			<GlobalSideNavigation
				{...sharedGlobalSideNavigationProps}
				ShortcutsSlot={InvitedAppShortcutsProductionisationSlot}
			/>
		);
	}

	// The invited-app-shortcuts slot is wired on values that are STABLE between the SSR render and the
	// client's first render — global-app-shortcuts access, hideGSN, and the Confluence-GSN rollout
	// gate (evaluated consistently on server and client). The divergent, cookie-derived eligibility +
	// experiment + kill-switch decision (`isInvitedAppShortcutsEnabled`) is carried into the slot via
	// context and applied INSIDE a TwoStageHydration boundary, so an SSR↔CSR flip of that decision can
	// never reach React's hydration reconciliation (PUMA-2188 / TWCG-460). An ineligible / kill-
	// switched cohort simply renders the plain (undecorated) shortcuts menu through the slot.
	if (showGlobalAppShortcuts && !hideGSN && fg('twcg-460-1p-notifications-cc-gsn-gate')) {
		// Render the invited-app shortcuts through the GSN `ShortcutsSlot` extension point (NAVX-5236)
		// rather than wrapping the whole nav and swapping its `shortcuts` config. The resolver result
		// and the enabled flag are passed to the (module-level, stable-identity) slot component via
		// context so GSN never remounts the shortcuts subtree on a resolver-result change.
		return (
			<InvitedAppShortcutsResolverContext.Provider value={invitedAppShortcutsContextValue}>
				<GlobalSideNavigation
					{...sharedGlobalSideNavigationProps}
					ShortcutsSlot={InvitedAppShortcutsSlot}
				/>
			</InvitedAppShortcutsResolverContext.Provider>
		);
	}
	return <GlobalSideNavigation {...sharedGlobalSideNavigationProps} />;
}
